/* eslint-disable max-len */
import { format, parse } from './date.js';

const weeks = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;

const getLabels = () => ({
    dayNamesShort: weeks.map((w) => w),
    dayNames: weeks.map((w) => w),
    monthNamesShort: months.map((m) => m),
    monthNames: months.map((m, i) => i + 1),
    amPm: ['am', 'pm'],
});

export const isDate = (date: unknown) => !(date == null || Number.isNaN(new Date(date as any).getTime()));
export const toDate = (date: unknown) => (isDate(date) ? new Date(date as any) : null);
export const isDateObject = (val: unknown): val is Date => val instanceof Date;

export const formatDate = (date: unknown, fmt?: string) => {
    const d = toDate(date);
    if (!d) return '';
    return format(d, fmt || 'yyyy-MM-dd', getLabels());
};
export const parseDate = (s: string, fmt?: string) => parse(s, fmt || 'yyyy-MM-dd', getLabels());

export const getDayCountOfMonth = (year: number, month: number) => {
    if ([3, 5, 8, 10].includes(month)) return 30;
    if (month === 1) return ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) ? 29 : 28;
    return 31;
};
export const getDayCountOfYear = (year: number) => ((year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) ? 366 : 365);

export const getFirstDayOfMonth = (date: Date) => {
    const d = new Date(date);
    d.setDate(1);
    return d.getDay();
};
export const prevDate = (date: Date, amount = 1) => new Date(date.getFullYear(), date.getMonth(), date.getDate() - amount);
export const nextDate = (date: Date, amount = 1) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);

export const getStartDateOfMonth = (year: number, month: number) => {
    const result = new Date(year, month, 1);
    const day = result.getDay();
    return day === 0 ? prevDate(result, 7) : prevDate(result, day);
};

export const range = (n: number) => Array.from({ length: n }, (_, i) => i);

export const modifyDate = (date: Date, y: number, m: number, d: number) => new Date(y, m, d, date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());

export const clearTime = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const prevMonth = (date: Date) => {
    let y = date.getFullYear();
    let m = date.getMonth();
    if (m === 0) {
        y -= 1;
        m = 11;
    } else {
        m -= 1;
    }
    const md = Math.min(date.getDate(), getDayCountOfMonth(y, m));
    return modifyDate(date, y, m, md);
};

export const nextMonth = (date: Date) => {
    let y = date.getFullYear();
    let m = date.getMonth();
    if (m === 11) {
        y += 1;
        m = 0;
    } else {
        m += 1;
    }
    const md = Math.min(date.getDate(), getDayCountOfMonth(y, m));
    return modifyDate(date, y, m, md);
};
export const thisMonth = (date: Date) => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const md = Math.min(date.getDate(), getDayCountOfMonth(y, m));
    return modifyDate(date, y, m, md);
};
export const prevYear = (date: Date, amount = 1) => {
    const y = date.getFullYear() - amount;
    const m = date.getMonth();
    const md = Math.min(date.getDate(), getDayCountOfMonth(y, m));
    return modifyDate(date, y, m, md);
};
export const nextYear = (date: Date, amount = 1) => {
    const y = date.getFullYear() + amount;
    const m = date.getMonth();
    const md = Math.min(date.getDate(), getDayCountOfMonth(y, m));
    return modifyDate(date, y, m, md);
};

// eslint-disable-next-line no-nested-ternary
export const minToMax = (a: number, b: number) => (a < b ? -1 : a > b ? 1 : 0);

const getMinMaxDate = (minDate: Date, maxDate: Date) => ({
    minDate: new Date(minDate.getFullYear(), minDate.getMonth(), 1),
    maxDate: new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0),
});

export const getLastMonths = (date: Date, n: number) => getMinMaxDate(
    new Date(new Date(date).setMonth(date.getMonth() - n)),
    new Date(new Date(date).setMonth(date.getMonth() - 1)),
);
export const getLastMonth = (value: Date) => {
    const d = prevMonth(value);
    return getMinMaxDate(d, d);
};
export const getNextMonth = (value: Date) => {
    const d = nextMonth(value);
    return getMinMaxDate(d, d);
};
export const getThisMonth = (value: Date) => {
    const d = thisMonth(value);
    return getMinMaxDate(d, d);
};
export const getNextMonths = (date: Date, n: number) => getMinMaxDate(
    new Date(new Date(date).setMonth(date.getMonth() + 1)),
    new Date(new Date(date).setMonth(date.getMonth() + n)),
);

export const getLastDay = (date: Date) => ({ minDate: new Date(date.getTime() - 24 * 3600 * 1000), maxDate: date });
export const getToday = (date: Date) => ({ minDate: date, maxDate: date });
export const getLastSevenDays = (date: Date) => ({ minDate: new Date(date.getTime() - 6 * 24 * 3600 * 1000), maxDate: date });
export const getLastThreeDays = (date: Date) => ({ minDate: new Date(date.getTime() - 2 * 24 * 3600 * 1000), maxDate: date });
export const getLastThirtyDays = (date: Date) => ({ minDate: new Date(date.getTime() - 29 * 24 * 3600 * 1000), maxDate: date });

export type PredefinedType =
  | 'lastTwelveMonths' | 'lastSixMonths' | 'lastThreeMonths' | 'lastMonth' | 'nextMonth' | 'thisMonth'
  | 'nextThreeMonths' | 'nextSixMonths' | 'today' | 'lastday' | 'lastDay'
  | 'lastseven' | 'lastSeven' | 'lastthirty' | 'lastThirty' | 'lastthree' | 'lastThree';

export const getPreDefinedDates = (type: PredefinedType, value?: Date | string | number) => {
    const date = value ? new Date(value) : new Date();
    switch (type) {
        case 'lastTwelveMonths': return getLastMonths(date, 12);
        case 'lastSixMonths': return getLastMonths(date, 6);
        case 'lastThreeMonths': return getLastMonths(date, 3);
        case 'lastMonth': return getLastMonth(date);
        case 'nextMonth': return getNextMonth(date);
        case 'thisMonth': return getThisMonth(date);
        case 'nextThreeMonths': return getNextMonths(date, 3);
        case 'nextSixMonths': return getNextMonths(date, 6);
        case 'today': return getToday(date);
        case 'lastday':
        case 'lastDay': return getLastDay(date);
        case 'lastseven':
        case 'lastSeven': return getLastSevenDays(date);
        case 'lastthirty':
        case 'lastThirty': return getLastThirtyDays(date);
        case 'lastthree':
        case 'lastThree': return getLastThreeDays(date);
        default: return { minDate: date, maxDate: date };
    }
};
